import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { WhatsappLogo, ChatCircleText, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * IntimationDialog
 * ----------------
 * Opens when a follow-up item's work_status flips to `submitted` or
 * `approved`. Shows a preview of the intimation message (built from the
 * admin-configurable template in /settings → intimation_templates) and
 * two action buttons:
 *   • WhatsApp — opens a `wa.me/<phone>?text=<msg>` deep-link in a new tab
 *   • SMS     — opens a native `sms:<phone>?body=<msg>` URI
 *
 * Variables substituted in the template:
 *   {customer_name} {service_name} {invoice_number} {date} {status}
 */
export default function IntimationDialog({
  open,
  onOpenChange,
  row,          // /processing row: { customer_name, customer_phone, service_name, invoice_number, ... }
  status,       // work-status value — drives which template is used
  statusLabel,  // human label for the status (e.g. 'Under Process')
  template,     // the raw template string for that status (from company settings)
}) {
  const [body, setBody] = useState('');
  const label = statusLabel || status || '';
  const fallback = 'Dear {customer_name},\n\nThe current status of your application for *{service_name}* (Invoice {invoice_number}) is *{status}* as on {date}.\n\nThank you!';

  const computed = useMemo(() => {
    if (!row) return '';
    const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    return (template || fallback)
      .replaceAll('{customer_name}', row.customer_name || '')
      .replaceAll('{service_name}', row.service_name || '')
      .replaceAll('{invoice_number}', row.invoice_number || '')
      .replaceAll('{date}', today)
      .replaceAll('{status}', label);
  }, [template, row, label]);

  // Reset the edit buffer every time the dialog opens with a new row /
  // status so a stale message never bleeds across invocations.
  useEffect(() => { if (open) setBody(computed); }, [open, computed]);

  if (!row) return null;

  // Phone digits-only — strip spaces / + / - so `wa.me` and `sms:`
  // accept the value. WhatsApp insists on no leading `+`; SMS doesn't
  // care either way.
  const phoneDigits = (row.customer_phone || '').replace(/\D/g, '');

  const sendWhatsapp = () => {
    if (!phoneDigits) {
      toast.error("Customer phone not on file — can't send WhatsApp");
      return;
    }
    const url = `https://wa.me/${phoneDigits}?text=${encodeURIComponent(body)}`;
    window.open(url, '_blank', 'noopener');
    onOpenChange?.(false);
  };

  const sendSms = () => {
    if (!phoneDigits) {
      toast.error("Customer phone not on file — can't send SMS");
      return;
    }
    // `sms:` accepts both web `?body=` and `&body=` query forms; we build
    // the spec-compliant one and let the OS handle it. Trigger via a
    // hidden anchor click instead of `window.location.href` so the parent
    // Follow-Up page stays put — on desktop browsers that don't handle
    // the `sms:` protocol natively, `window.location.href` was navigating
    // AWAY from the queue, losing the operator's context. The anchor
    // click pattern hands the URI to the OS handler without touching the
    // current document's location.
    const url = `sms:${phoneDigits}?body=${encodeURIComponent(body)}`;
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    // No target='_blank' — SMS handlers open external apps regardless.
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-lg" data-testid="intimation-dialog">
        <DialogHeader>
          <DialogTitle>Send {label} intimation</DialogTitle>
          <DialogDescription>
            Notify {row.customer_name} about <span className="font-semibold text-[#0A0A0A]">{row.service_name}</span> ({row.invoice_number}).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-[#E4E4E7] bg-[#FAFAFA] p-3 text-xs space-y-0.5">
            <div className="flex justify-between text-[#52525B]"><span>Customer</span><span className="font-semibold text-[#0A0A0A]">{row.customer_name}</span></div>
            <div className="flex justify-between text-[#52525B]"><span>Mobile</span><span className="font-mono">{phoneDigits || <span className="text-[#EF4444] italic">— not on file —</span>}</span></div>
            <div className="flex justify-between text-[#52525B]"><span>Service</span><span className="font-semibold text-[#0A0A0A]">{row.service_name}</span></div>
            <div className="flex justify-between text-[#52525B]"><span>Date</span><span>{new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span></div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Message (editable)</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              className="mt-1 rounded-md text-xs leading-relaxed font-mono"
              data-testid="intimation-body"
            />
            <p className="text-[10px] text-[#A1A1AA] mt-1">
              Tweak as needed — the template default lives in <span className="font-mono">Settings → Intimation Templates</span>.
            </p>
          </div>
        </div>

        <DialogFooter className="flex flex-row items-center gap-2 sm:justify-between">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange?.(false)} className="rounded-lg text-xs" data-testid="intimation-skip-btn">
            <X size={14} className="mr-1" /> Skip
          </Button>
          <div className="flex items-center gap-2">
            <Button onClick={sendSms} variant="outline" size="sm"
              className="rounded-lg text-xs border-[#3B82F6] text-[#3B82F6] hover:bg-[#EFF6FF]"
              data-testid="intimation-sms-btn"
              disabled={!phoneDigits}>
              <ChatCircleText size={14} className="mr-1" weight="duotone" /> SMS
            </Button>
            <Button onClick={sendWhatsapp} size="sm"
              className="rounded-lg text-xs bg-[#25D366] text-white hover:bg-[#1FA855] hover:text-white"
              data-testid="intimation-whatsapp-btn"
              disabled={!phoneDigits}>
              <WhatsappLogo size={14} className="mr-1" weight="duotone" /> WhatsApp
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
